import React from 'react';
import PropTypes from 'prop-types';
import CloseModalButton from './CloseModalButton';
import DontShowAgain from '../../../DontShowAgain/DontShowAgain';
import useDontShowAgainMessageRead from '../../../DontShowAgain/useDontShowAgainMessageRead';
import Item from './Item';
import { createClosedByDontShowAgainEvent } from './utils';

/**
Un item di `Modal`.

Visualizza un contenuto che blocca temporaneamente le interazioni con il resto dell'interfaccia.

Si può utilizzare per questi tipi di messaggi:

- Chiedere conferma o fare decisioni
- Fornire delle informazioni
- Completare dei compiti importanti all'interno di un processo

_Nota_: Se la modale non contiene delle azioni (ad es. Salva),
ma contiene ad es. un processo che richiede i pulsanti "Indietro" e "Avanti",
bisogna inserire il pulsante "X" per poterla chiudere.

_Esempio struttura del componente_

```js
<Modal.Item ... >
  <Modal.Header>
    ...
  </Modal.Header>

  <Modal.Body>
    ...
  </Modal.Body>

  <Modal.Footer>
    ...
  </Modal.Footer>
</Modal.Item>
```
*/
const GenericItem = ({
  id, className, children, onClose, dontShowAgainId, dontShowAgainMessage,
}) => {
  const messageRead = useDontShowAgainMessageRead(dontShowAgainId, true);

  if (messageRead) {
    onClose && onClose(createClosedByDontShowAgainEvent(id, dontShowAgainId));
    return null;
  }
  return (
    <Item id={id} className={className}>
      {onClose && <CloseModalButton onClose={onClose} />}
      {children}
      {dontShowAgainId && <DontShowAgain className="v2-modal__dontshowagain" id={dontShowAgainId} message={dontShowAgainMessage} />}
    </Item>
  );
};

GenericItem.propTypes = {
  /** Classi css personalizzate da aggiungere al componente */
  className: PropTypes.string,
  /** Contenuto da renderizzare all'interno del componente.
 * Può essere di tipo `Modal.Header`, `Modal.Body` o `Modal.Footer`.
 */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /**
   * Identificativo dell'item.
   */
  id: PropTypes.string.isRequired,
  /**
Callback di chiusura della modale.

Il primo parametro è l'evento scatenante.

Se la chiusura avviene a seguito della gestione dontShowAgain, ritorna un oggetto con `id` pari all'id della modale e
`dontShowAgainId` pari alla prop `dontShowAgainId` della modale.
Per verificate che la chiusura sia conseguenza della gestione dontShowAgain, usare il metodo `Modal.isClosedByDontShowAgainEvent`.
   */
  onClose: PropTypes.func,

  /**
Identificativo del componente per la gestione 'Non visualizzare più'. Se impostato abilita la gestione.
Lo stato è salvato nella sessione locale del browser quindi è necessario un identificativo per gestire il salvataggio.

Tipicamente è composto dall'id dell'utente e da un identificativo del messaggio.

Utilizzare `Modal.clearMessageStatus` per pulire lo stato.
*/
  dontShowAgainId: PropTypes.string,

  /**
Messaggio del componente per la gestione 'Non visualizzare più'.
*/
  dontShowAgainMessage: PropTypes.string,
};

GenericItem.defaultProps = {
  className: undefined,
  children: undefined,
  onClose: undefined,
  dontShowAgainId: undefined,
  dontShowAgainMessage: 'Non mostrare più questo messaggio',
};

export default GenericItem;
